import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Step,
  StepLabel,
  Stepper,
  Typography as T,
  useMediaQuery,
} from "@mui/material";
import { isEmpty } from "ramda";

import { useTypedTheme } from "hooks";
import { useStoreActions, useStoreState } from "store/store";
import { palette } from "theme/palette";

import { StepperConnector } from "./components/stepper-connector";
import { StepIconComponent } from "./components/stepper-icon";
import {
  BOOKING_STEPS,
  CUSTOM_LOCATION_STEPS,
  FLIGHT_STEPS,
  INITIAL_STEPS,
  SHUTTLE_STEPS,
} from "./constants";

const StepperComponent = () => {
  // styles
  const { theme } = useTypedTheme();
  // actions
  const setSteps = useStoreActions((actions) => actions.setSteps);
  // state
  const stepperRef = useRef<HTMLDivElement>(null);
  const { steps } = useStoreState((state) => state);
  const isCustomDestination = useStoreState(
    (state) => state.isCustomDestination
  );
  const isLocationAirport = useStoreState((state) => state.isLocationAirport);
  const isDestinationAirport = useStoreState(
    (state) => state.isDestinationAirport
  );
  const selectedCar = useStoreState((s) => s.selectedCar);

  const [activeStep, setActiveStep] = useState<number>(0);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const matches = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const currentLocation = location.pathname;
  const isShuttle = selectedCar?.isShuttle;

  useEffect(() => {
    const regularStepsHistory = [
      "/step-1",
      "/step-2",
      "/step-3",
      "/step-4",
      "/step-5",
      "/step-6",
      "/step-7",
    ];
    const airBnbStepsHistory = ["/step-1", "/step-3", "/step-5", "/step-7"];
    const shuttleStepsHistory = [
      "/step-1",
      "/step-2",
      "/step-3",
      "/step-5",
      "/step-6",
      "/step-7",
    ];

    const usedArray = isCustomDestination
      ? airBnbStepsHistory
      : isShuttle
        ? shuttleStepsHistory
        : regularStepsHistory;

    setActiveStep(usedArray.indexOf(currentLocation));
  }, [isCustomDestination, currentLocation]);

  useEffect(() => {
    if (isEmpty(steps)) {
      setSteps(INITIAL_STEPS);
    }

    stepperRef?.current?.scrollIntoView();
  }, []);

  useEffect(() => {
    if (isCustomDestination) {
      setSteps(CUSTOM_LOCATION_STEPS);
      return;
    }

    if (isDestinationAirport || isLocationAirport) {
      if (isShuttle) {
        setSteps(SHUTTLE_STEPS);
      } else {
        setSteps(FLIGHT_STEPS);
      }
    } else {
      setSteps(BOOKING_STEPS);
    }
  }, [isDestinationAirport, isLocationAirport, isShuttle]);

  return (
    <div>
      <Box
        sx={{
          paddingLeft: isMobile ? "3%" : "10%",
          paddingRight: isMobile ? "3%" : "10%",
          paddingTop: 5,
          paddingBottom: "10px",
        }}
        ref={stepperRef}
      >
        <Stepper activeStep={activeStep} connector={<StepperConnector />}>
          {steps.map((label, i) => {
            return (
              <Step key={label}>
                <StepLabel
                  StepIconComponent={StepIconComponent}
                  optional={
                    !isMobile ? (
                      activeStep >= i ? (
                        <T
                          variant="stepperTitle"
                          sx={{
                            maxWidth: "80px",
                            color: palette.accent,
                            display: "flex",
                            fontWeight: 700,
                            ...(matches && { fontSize: "11px" }),
                          }}
                        >
                          {activeStep === i || !matches ? label : ""}
                        </T>
                      ) : (
                        <T
                          variant="stepperTitle"
                          sx={{
                            maxWidth: "80px",
                            color: palette.grey,
                            display: "flex",
                          }}
                        >
                          {!matches ? label : ""}
                        </T>
                      )
                    ) : null
                  }
                />
              </Step>
            );
          })}
        </Stepper>
      </Box>
    </div>
  );
};

export default StepperComponent;
